/*
 * welcome10-capture.js — restores the WELCOME10 code-reveal that has never worked in the cart drawer.
 *
 * THE BUG (found + confirmed 2026-07-25)
 *   The reveal handler lived in an inline <script> inside snippets/cart-drawer.liquid, wrapped in
 *   `{% if shangmeng_cart_eligible_count > 0 %}`. But this theme injects the cart drawer through
 *   `innerHTML` into #ajax-loading-cart — and innerHTML NEVER executes injected <script> tags.
 *   Server-rendered HTML on /, /pages/*, PDP and /collections/* contains 0 occurrences of both
 *   `CartDrawerWelcome10Form` and `shangmengCartWelcomeCodeInit`, so the listener was never bound
 *   for anyone, on any visit, since the feature shipped.
 *
 *   RETRACTED as evidence: an earlier note here cited a CDP probe showing
 *   `typeof window.shangmengCartWelcomeCodeInit === "undefined"`. That probe was INVALID —
 *   `chrome.mjs eval` runs in a `Page.createIsolatedWorld` context (deliberate, for stealth), where
 *   page-world globals are never visible: the same probe reports `window.Shopify` and
 *   `window.Alpine` as undefined too, and both certainly exist. Isolated-world eval can read and
 *   mutate the shared DOM, but it can NEVER be used to assert the presence or absence of page JS
 *   globals. The diagnosis rests on the server-rendered-HTML evidence above plus the positive
 *   control below, not on that probe.
 *
 *   POSITIVE CONTROL (/cart page, same mechanism, script server-rendered so it does run): dispatching
 *   a submit with the native POST blocked flipped `#cart-page-welcome10-code` hidden true -> false and
 *   the button text to "WELCOME10". So the mechanism itself is sound; only the drawer's binding was
 *   missing. DOM effects are cross-world visible, which is why this control is valid where the
 *   global-variable probe was not.
 *
 *   What still worked, and why that masked it: the <form> is a native Shopify `customer` form
 *   targeting a hidden iframe, so submitting it created the customer and fired the welcome email
 *   with no JS at all. The shopper got the email but never saw the code on screen — and Omnisend
 *   never received `identifyContact`, so the `cart_welcome10` tag was never applied either.
 *
 * THE FIX
 *   Bind the same handler from a layout-loaded asset, delegated on `document`, so it survives every
 *   AJAX re-render. The three surfaces use DIFFERENT init flags (see the table below), so setting the
 *   drawer's flag here cannot disable the healthy /cart and PDP handlers — and the matcher is scoped
 *   to the drawer form so those two never see a duplicate `omnisend.push`. Zero Liquid edits.
 *
 * AUTO-APPLY (drawer upgraded 2026-07-27)
 *   The landing page promises "Buy 4 Get 1 Free + 10% off your first order ≈ 27% off", but the cart
 *   surfaces used to only reveal the code TEXT — the shopper still had to reach checkout and retype
 *   it, while the PDP had one-click apply. That gap sat between the promise and its redemption.
 *   The drawer now waits for the hidden customer-form iframe to finish, then navigates through
 *   `/discount/WELCOME10?redirect=<current path>&_sm_welcome10=added`. This preserves the email
 *   capture before applying the code and removes a redundant second shopper click.
 *
 *   The attribute is deliberately NOT the PDP's `data-shangmeng-welcome-apply`: the PDP state machine
 *   runs a document-wide querySelectorAll on its own attribute, and the drawer is present in the DOM
 *   of every PDP, so a shared attribute would let a PDP email submit reveal the drawer's button.
 *
 *   The six-locale copy was rewritten in the same change ("Apply it below or enter it at checkout"),
 *   because "enter it at checkout" stopped being the whole truth the moment a button existed.
 */
(function () {
  if (window.shangmengCartWelcomeCodeInit) return;
  window.shangmengCartWelcomeCodeInit = true;

  // SCOPED TO THE DRAWER ONLY — deliberately narrow. There are three WELCOME10 surfaces, each with
  // its own form id, reveal element and init flag (verified 2026-07-25):
  //
  //   surface            form id                    reveal element                 init flag
  //   cart drawer        CartDrawerWelcome10Form    #cart-drawer-welcome10-code     shangmengCartWelcomeCodeInit
  //   /cart page         CartPageWelcome10Form      #cart-page-welcome10-code       shangmengCartPageWelcomeCodeInit
  //   PDP                PdpWelcome10Form           [data-shangmeng-welcome-apply]  shangmengWelcomeCodeInit
  //
  // /cart and PDP are server-rendered, so their inline scripts DO run and both work — verified on
  // /cart by dispatching a submit with the native POST blocked: code hidden true -> false and the
  // button flipped to "WELCOME10". Only the drawer is broken. A generic `form[id*="Welcome10Form"]`
  // matcher would therefore fire on all three and double the `omnisend.push` on the two healthy
  // surfaces, so this handler touches the drawer form and nothing else.
  function welcomeForm(target) {
    if (!target || !target.closest) return null;
    return target.closest('#CartDrawerWelcome10Form');
  }

  // Return path for the /discount/ round trip. Computed from `location` rather than baked into
  // Liquid because the drawer is rendered in an AJAX context (its server-side `request` is not the
  // shopper's page) and because this keeps the locale prefix correct for free.
  function applyHref() {
    var url = new URL(window.location.href);
    url.searchParams.set('_sm_welcome10', 'added');
    return '/discount/WELCOME10?redirect=' + encodeURIComponent(url.pathname + url.search);
  }

  function refreshApplyLinks() {
    var href = applyHref();
    document.querySelectorAll('[data-shangmeng-cart-welcome-apply]').forEach(function (link) {
      link.setAttribute('href', href);
    });
  }

  function revealIn(form) {
    var scope = form.parentElement || document;
    var code = scope.querySelector('[data-shangmeng-cart-welcome-code]')
            || document.getElementById('cart-drawer-welcome10-code');
    if (code) { code.hidden = false; code.removeAttribute('hidden'); }
    var submit = form.querySelector('[data-shangmeng-cart-code-submit]')
              || form.querySelector('button[type="submit"]');
    if (submit) {
      submit.textContent = 'Applying WELCOME10…';
      submit.disabled = true;
    }
    return !!code;
  }

  function autoApplyAfterCapture(form) {
    var navigated = false;
    var fallbackTimer = null;
    function applyNow() {
      if (navigated) return;
      navigated = true;
      if (fallbackTimer) window.clearTimeout(fallbackTimer);
      window.location.assign(applyHref());
    }

    var frame = null;
    var targetName = form.getAttribute('target');
    if (targetName) {
      var targets = document.getElementsByName(targetName);
      for (var index = 0; index < targets.length; index += 1) {
        if (targets[index].tagName === 'IFRAME') {
          frame = targets[index];
          break;
        }
      }
    }
    if (frame) frame.addEventListener('load', applyNow, { once: true });
    // Fail open for the shopper if the iframe load event is suppressed by an extension.
    fallbackTimer = window.setTimeout(applyNow, 2200);
  }

  document.addEventListener('submit', function (event) {
    var form = welcomeForm(event.target);
    if (!form) return;

    var input = form.querySelector('input[type="email"]');
    if (!input || !input.checkValidity()) {
      event.preventDefault();
      if (input) input.reportValidity();
      return;
    }

    // Let the native customer-form POST proceed into its hidden iframe (that is what creates the
    // Shopify customer and triggers the welcome email) — we only add the client-side effects.
    try {
      window.omnisend = window.omnisend || [];
      window.omnisend.push(['identifyContact', {
        email: input.value.trim(),
        tags: ['cart_welcome10'],
        channels: { email: { status: 'subscribed', statusDate: new Date().toISOString() } }
      }]);
    } catch (e) {}

    try {
      revealIn(form);
      autoApplyAfterCapture(form);
    } catch (e) {}
  }, false);

  // Keep hrefs current for buttons that were revealed by the /cart page's own inline script (that
  // script reveals but does not compute the return path), and for any AJAX re-render.
  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  onReady(function () {
    try { refreshApplyLinks(); } catch (e) {}
    // Strip the return marker so it neither sticks in the address bar nor gets shared/bookmarked.
    // The discount itself is already on the cart at this point, so nothing depends on the parameter.
    try {
      var url = new URL(window.location.href);
      if (url.searchParams.get('_sm_welcome10') === 'added') {
        url.searchParams.delete('_sm_welcome10');
        window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
      }
    } catch (e) {}
  });

  // Refresh immediately before navigation: the drawer can be opened, then the shopper can navigate
  // with the drawer's markup persisting, which would otherwise leave a stale redirect target.
  document.addEventListener('click', function (event) {
    if (!event.target || !event.target.closest) return;
    var link = event.target.closest('[data-shangmeng-cart-welcome-apply]');
    if (!link) return;
    try { link.setAttribute('href', applyHref()); } catch (e) {}
  }, true);
})();

/* welcome10-capture guard rev-20260727a drawer capture then auto-apply */

/* nf-page welcome10 binding rev-20260821a: NF landing page (/pages/nail-freedom) only.
   Guard = #nf-lp presence; PDP keeps its own template-scoped handler, drawer keeps
   #CartDrawerWelcome10Form above. Native POST proceeds into the hidden iframe. */
(function () {
  function init() {
    if (!document.querySelector('#nf-lp')) return;
    var form = document.getElementById('PdpWelcome10Form');
    if (!form || form.dataset.nfBound) return;
    form.dataset.nfBound = '1';
    form.addEventListener('submit', function () {
      var i = form.querySelector('input[type="email"]');
      if (!i || !i.checkValidity()) return;
      try {
        window.omnisend = window.omnisend || [];
        window.omnisend.push(['identifyContact', { email: i.value.trim(), tags: ['pdp_welcome10'],
          channels: { email: { status: 'subscribed', statusDate: new Date().toISOString() } } }]);
      } catch (e) {}
      var code = document.getElementById('pdp-offer-inline-code');
      if (code) code.hidden = false;
      form.hidden = true;
    }, false);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();

